/**
 * Metrics Service for Arkadium Ads
 * Unified metrics collection and reporting for Display and Video ads.
 */
import { RafJankMeter } from "./internals/raf-jank-meter.js";
import { readMemorySnapshot } from "./internals/memory-snapshot-meter.js";
import { bytesToMb, generateHexId, getBrowserInfo, getCurrentScriptSrc, getHostname, isFiniteNumber, sanitizePath, stableSample } from "./internals/utils.js";
import { getGeo } from "../core/geo.js";
// Config Constants
// T = Type & Target: Defines data type and where it goes in the payload
const T = {
    N: "number", // Value is a number
    S: "string", // Value is a string
    M: "measurement", // Send as Sentry 'measurement' (aggregatable)
    G: "tag", // Send as Sentry 'tag' (filterable)
    P: "span" // Send as Sentry 'span.data' (contextual)
};
// G = Group: Metric namespace prefix (e.g. ads.timing.*)
const G = { TIME: "timing", MEM: "mem", FPS: "fps", CTX: "context" };
// U = Unit: Measurement unit for Sentry
const U = { MS: "millisecond", HZ: "hertz", NONE: "none" };
// Metric Definitions: [Type, Targets[], Unit?, Group?]
// Format: key: [DataType, [Destinations], Unit, GroupPrefix]
const CFG = {
    ttfr: [T.N, [T.M, T.P], U.MS, G.TIME],
    ttlr: [T.N, [T.M, T.P], U.MS, G.TIME],
    ttb: [T.N, [T.M, T.P], U.MS, G.TIME],
    page_to_first_ad: [T.N, [T.M, T.P], U.MS, G.TIME],
    page_to_last_ad: [T.N, [T.M, T.P], U.MS, G.TIME],
    script_exec: [T.N, [T.M, T.P], U.MS, G.TIME],
    memory_delta: [T.N, [T.M, T.P], U.NONE, G.MEM],
    memory_used: [T.N, [T.M, T.P], U.NONE, G.MEM],
    fps_avg: [T.N, [T.M, T.P], U.HZ, G.FPS],
    fps_std: [T.N, [T.M, T.P], U.MS, G.FPS],
    jank_count: [T.N, [T.M, T.P], U.NONE, G.FPS],
    max_frame_time: [T.N, [T.P], U.MS, G.FPS],
    batch_size: [T.N, [T.G, T.P], undefined, G.CTX],
    config_id: [T.S, [T.G, T.P], undefined, G.CTX],
    page_host: [T.S, [T.G, T.P], undefined, G.CTX],
    page_path: [T.S, [T.G, T.P], undefined, G.CTX],
    script_path: [T.S, [T.G, T.P], undefined, G.CTX],
    sample_rate: [T.N, [T.G, T.P], undefined, G.CTX],
    ABTestSlot1: [T.S, [T.G, T.P], undefined, G.CTX],
    ABTestRefreshGroup: [T.S, [T.G, T.P], undefined, G.CTX],
    // Generic ad error context (video/display)
    adError: [T.S, [T.P], undefined, G.CTX],
    adErrorCode: [T.S, [T.P], undefined, G.CTX],
    adErrorTrace: [T.S, [T.P], undefined, G.CTX],
    adRetryCount: [T.S, [T.G, T.P], undefined, G.CTX],
    adBidder: [T.S, [T.G, T.P], undefined, G.CTX],
    adCpm: [T.N, [T.M, T.G, T.P], undefined, G.CTX],
    adPlayer: [T.S, [T.G, T.P], undefined, G.CTX],
    ad_stage: [T.S, [T.G, T.P], undefined, G.CTX],
    ad_stage_history: [T.S, [T.G, T.P], undefined, G.CTX],
    finish_reason: [T.S, [T.G, T.P], undefined, G.CTX],
    screen_width: [T.N, [T.P], undefined, G.CTX],
    screen_height: [T.N, [T.P], undefined, G.CTX],
    browser_name: [T.S, [T.P], undefined, G.CTX],
    browser_version: [T.S, [T.P], undefined, G.CTX],
    // Geo context (used for performance breakdowns in Sentry)
    country: [T.S, [T.G, T.P], undefined, G.CTX],
    // Focus restore feature observability (per-batch counters; not tagged to avoid high-cardinality numeric tags)
    focus_lost_count: [T.N, [T.M, T.P], U.NONE, G.CTX],
    focus_restored_count: [T.N, [T.M, T.P], U.NONE, G.CTX],
};
// Helpers & Context
export const DEFAULT_MAX_BATCH_DURATION_MS = 15000;
const normalizeTimeout = (ms) => {
    const v = typeof ms === "number" ? ms : null;
    if (v != null && Number.isFinite(v) && v > 0)
        return v;
    return DEFAULT_MAX_BATCH_DURATION_MS;
};
const safe = (fn) => { try {
    return fn();
}
catch {
    return undefined;
} };
const timeOrigin = safe(() => performance.timeOrigin) ?? (Date.now() - (safe(() => performance.now()) ?? 0));
const now = () => safe(() => performance.now()) ?? Date.now() - timeOrigin;
const scriptSrc = getCurrentScriptSrc();
const envMatch = scriptSrc?.match(/\/(dev3?|dev2?|canary|alpha|stable|prod)\//i)?.[1];
const isLocal = scriptSrc?.includes("localhost") || scriptSrc?.includes("127.0.0.1");
const env = (envMatch ?? (isLocal ? "local" : "unknown")).toLowerCase();
const browser = safe(() => getBrowserInfo()) ?? { name: "Other" };
const scriptExec = Math.round(now());
// Module-level Trace ID (persistent for page session)
const PAGE_TRACE_ID = generateHexId(16);
export function createMetricsService(config) {
    const maxBatchDurationMs = normalizeTimeout(config.renderTimeoutMs);
    const state = {
        enabled: false,
        rate: 0.05, // Default 5% to match legacy
        params: {},
        country: undefined,
        start: undefined,
        first: undefined,
        last: undefined,
        units: undefined,
        count: 0,
        ids: new Set(),
        mem: undefined,
        ttb: undefined,
        tId: undefined,
        raf: undefined,
        cfgId: undefined,
        finishReason: undefined,
        // Session-level focus counters: accumulate across cycles, drained on finish().
        // Decoupled from cycle teardown so events between batches survive until the
        // next emitted batch picks them up.
        focusLostCount: 0,
        focusRestoredCount: 0
    };
    // Best-effort geo enrichment — fires immediately so the promise resolves
    // well before finish(). getGeo() is cached on window.__ark_ads__, so if
    // ads-display/ads-react already started a geo fetch this is a no-op.
    let geoStarted = false;
    const loadCountry = () => {
        if (geoStarted || state.country)
            return;
        geoStarted = true;
        try {
            getGeo()
                .then((geo) => {
                const code = geo?.country_code;
                if (state.country)
                    return; // Don't override explicit countryLoaded()
                if (typeof code === "string" && code.trim())
                    state.country = code.trim();
            })
                .catch(() => { });
        }
        catch { }
    };
    loadCountry();
    const teardown = () => {
        state.raf?.stop();
        state.raf?.destroy();
        state.raf = undefined;
        if (state.tId)
            clearTimeout(state.tId);
        state.tId = undefined;
        safe(() => {
            performance.getEntriesByType("mark").forEach(e => e.name.startsWith("ads:") && performance.clearMarks(e.name));
            performance.getEntriesByType("measure").forEach(e => e.name.startsWith("ads:") && performance.clearMeasures(e.name));
        });
        state.start = state.first = state.last = state.units = state.mem = state.ttb = undefined;
        state.count = 0;
        state.ids.clear();
        state.finishReason = undefined;
    };
    const setRate = (r) => {
        const v = typeof r === "number" && Number.isFinite(r) && r >= 0 ? r : 0.05;
        state.rate = v > 1 ? 1 : v;
        state.enabled = state.rate > 0 && (safe(() => window.self === window.top) ?? false);
        if (!state.enabled)
            teardown();
    };
    const finish = (lastRender, reason) => {
        if (!isFiniteNumber(state.start))
            return;
        if (state.tId)
            clearTimeout(state.tId);
        const raf = state.raf ? (safe(() => state.raf.getCurrentMetrics()) ?? {}) : {};
        state.raf?.stop();
        state.raf?.destroy();
        state.raf = undefined;
        const endMem = readMemorySnapshot()?.usedJSHeapSize;
        const memDelta = isFiniteNumber(state.mem) && isFiniteNumber(endMem) ? endMem - state.mem : undefined;
        const finishReason = reason ?? state.finishReason ?? state.params.finish_reason;
        const finishStage = state.params.ad_stage ?? finishReason;
        const finishStageHistory = state.params.ad_stage_history;
        const m = {
            ttfr: isFiniteNumber(state.first) && state.first >= state.start ? Math.round(state.first - state.start) : undefined,
            ttlr: (state.last ?? lastRender) >= state.start ? Math.round((state.last ?? lastRender) - state.start) : undefined,
            ttb: state.ttb,
            script_exec: scriptExec,
            memory_used: bytesToMb(endMem),
            memory_delta: bytesToMb(memDelta),
            fps_avg: raf.avgHz,
            fps_std: raf.stdDevFrameDeltaMs,
            jank_count: raf.jankCount ?? 0,
            max_frame_time: raf.maxFrameDeltaMs,
            batch_size: state.units,
            config_id: state.cfgId,
            page_host: getHostname(),
            page_path: sanitizePath(location.pathname),
            script_path: scriptSrc ? new URL(scriptSrc, location.href).pathname.replace(/\.js$/i, "") : undefined,
            sample_rate: state.rate,
            screen_width: safe(() => screen.width),
            screen_height: safe(() => screen.height),
            browser_name: browser.name,
            browser_version: browser.version,
            focus_lost_count: state.focusLostCount,
            focus_restored_count: state.focusRestoredCount,
            ...state.params
        };
        state.focusLostCount = 0;
        state.focusRestoredCount = 0;
        const country = state.params.country ?? state.country;
        if (typeof country === "string" && country.trim())
            m.country = country;
        m.finish_reason = finishReason ?? m.finish_reason;
        m.ad_stage = finishStage ?? m.ad_stage;
        m.ad_stage_history = finishStageHistory ?? m.ad_stage_history;
        if (isFiniteNumber(m.ttfr))
            m.page_to_first_ad = Math.round(scriptExec + m.ttfr);
        if (isFiniteNumber(m.ttlr))
            m.page_to_last_ad = Math.round(scriptExec + m.ttlr);
        // Use persistent PAGE_TRACE_ID for sampling and payload
        if (state.enabled && state.rate > 0 && stableSample(PAGE_TRACE_ID, state.rate)) {
            send(config, m, state.start, PAGE_TRACE_ID);
        }
        teardown();
    };
    setRate(config.sampleRate);
    return {
        configLoaded: (id) => { if (state.enabled && id != null)
            safe(() => state.cfgId = String(id)); },
        countryLoaded: (cc) => {
            if (!state.enabled || cc == null)
                return;
            const v = String(cc).trim();
            if (!v)
                return;
            state.country = v;
        },
        setSampleRate: setRate,
        setCustomParams: (p) => {
            if (!state.enabled)
                return;
            state.params = {};
            if (!p || typeof p !== "object")
                return;
            const entries = Array.isArray(p) ? p : Object.entries(p);
            for (const entry of entries) {
                if (!Array.isArray(entry))
                    continue;
                const [k, v] = entry;
                if (typeof v === "number" && isFiniteNumber(v)) {
                    state.params[k] = String(v);
                }
                else if (typeof v === "string" && v.trim()) {
                    state.params[k] = v;
                }
            }
        },
        beginCycle: (n) => {
            if (!state.enabled)
                return;
            teardown();
            safe(() => performance.mark("ads:batch:start"));
            state.start = now();
            state.units = n;
            state.mem = readMemorySnapshot()?.usedJSHeapSize ?? undefined;
            state.raf = new RafJankMeter({ jankThresholdMs: 16.7, pauseOnHidden: true });
            safe(() => state.raf.start());
            state.finishReason = undefined;
            state.tId = window.setTimeout(() => {
                state.finishReason = state.finishReason ?? "timeout";
                finish(now(), state.finishReason);
            }, maxBatchDurationMs);
        },
        trackBidsComplete: () => {
            if (!state.enabled || !isFiniteNumber(state.start))
                return;
            safe(() => {
                performance.mark("ads:batch:bids:complete");
                performance.measure("ads:batch:ttb", "ads:batch:start", "ads:batch:bids:complete");
                const m = performance.getEntriesByName("ads:batch:ttb")[0];
                state.ttb = isFiniteNumber(m?.duration) ? Math.round(m.duration) : undefined;
            });
        },
        trackRenderComplete: (id) => {
            if (!state.enabled || !id || state.ids.has(id) || !isFiniteNumber(state.start))
                return;
            const t = now();
            state.ids.add(id);
            state.count++;
            safe(() => {
                const mark = `ads:render:${id}`;
                performance.mark(mark);
                if (!isFiniteNumber(state.first)) {
                    state.first = t;
                    performance.measure("ads:batch:ttfr", "ads:batch:start", mark);
                }
            });
            state.last = t;
            if (!state.finishReason && !state.params.finish_reason)
                state.finishReason = "render_complete";
            if (isFiniteNumber(state.units) && state.count === state.units)
                finish(t, state.finishReason);
        },
        trackFocusLost: () => {
            if (!state.enabled)
                return;
            state.focusLostCount++;
        },
        trackFocusRestored: () => {
            if (!state.enabled)
                return;
            state.focusRestoredCount++;
        },
        cleanup: teardown,
        trackError: (error, tags) => {
            safe(() => sendException(config, error, tags));
        }
    };
}
function send(cfg, metrics, start, traceId) {
    const measurements = {}, tags = { environment: env }, spanData = {};
    for (const [k, v] of Object.entries(metrics)) {
        if (v == null)
            continue;
        const c = CFG[k];
        if (!c)
            continue;
        const [type, targets, unit, group] = c;
        const val = type === T.N ? Number(v) : v;
        if (type === T.N && !isFiniteNumber(val))
            continue;
        let name = k;
        if (group && k.startsWith(`${group}_`))
            name = k.substring(group.length + 1);
        if (group === G.MEM && unit === U.NONE && !name.endsWith("_mb"))
            name += "_mb";
        const key = group ? `ads.${group}.${name}` : `ads.${name}`;
        if (targets.includes(T.M) && type === T.N)
            measurements[key] = { value: val, unit: unit || "none" };
        if (targets.includes(T.G))
            tags[key] = String(val);
        if (targets.includes(T.P))
            spanData[key] = val;
    }
    const rootSpanId = generateHexId(8);
    const childSpanId = generateHexId(8);
    const eventId = generateHexId(16);
    const envelope = JSON.stringify({ dsn: cfg.dsn, sent_at: new Date().toISOString(), event_id: eventId, sdk: { name: cfg.sdkName, version: cfg.version } }) + "\n" +
        JSON.stringify({ type: "transaction" }) + "\n" +
        JSON.stringify({
            event_id: eventId,
            type: "transaction",
            transaction: cfg.transactionName,
            transaction_info: { source: "custom" },
            platform: "javascript",
            environment: env,
            release: `${cfg.sdkName}@${cfg.version}`,
            start_timestamp: timeOrigin / 1000,
            timestamp: Date.now() / 1000,
            user: metrics.country ? { geo: { country_code: metrics.country } } : undefined,
            contexts: { trace: { trace_id: traceId, span_id: rootSpanId, op: "ads.display.batch", description: `Batch of ${metrics.batch_size ?? "unknown"} ads`, status: "ok" }, browser },
            tags, measurements, spans: [{ span_id: childSpanId, trace_id: traceId, parent_span_id: rootSpanId, op: "ads.batch.load", description: `Load batch of ${metrics.batch_size ?? "unknown"} ads`, status: "ok", start_timestamp: (timeOrigin + start) / 1000, timestamp: Date.now() / 1000, data: spanData }],
            sdk: { name: cfg.sdkName, version: cfg.version }
        });
    const url = cfg.dsn.replace(/^https:\/\/[^@]+@([^/]+)\/.*$/, `https://$1/api/${cfg.projectId}/envelope/`);
    fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-sentry-envelope" },
        body: envelope,
        keepalive: true
    }).then(res => { if (!res.ok)
        throw new Error("Fetch Failed"); }).catch(() => { try {
        navigator.sendBeacon?.(url, new Blob([envelope], { type: "application/x-sentry-envelope" }));
    }
    catch { } });
}
// A real error event (not a "transaction" span), sent unsampled - see trackError on
// IMetricsService for why this deliberately bypasses the batch-metrics sampling/state
// machine above. Same envelope/URL/keepalive+sendBeacon-fallback shape as send().
function sendException(cfg, error, tags) {
    const eventId = generateHexId(16);
    const envelope = JSON.stringify({ dsn: cfg.dsn, sent_at: new Date().toISOString(), event_id: eventId, sdk: { name: cfg.sdkName, version: cfg.version } }) + "\n" +
        JSON.stringify({ type: "event" }) + "\n" +
        JSON.stringify({
            event_id: eventId,
            type: "event",
            level: "error",
            platform: "javascript",
            environment: env,
            release: `${cfg.sdkName}@${cfg.version}`,
            timestamp: Date.now() / 1000,
            exception: { values: [{ type: error.name || "Error", value: error.message }] },
            tags: { environment: env, ...tags },
            contexts: { browser },
            sdk: { name: cfg.sdkName, version: cfg.version }
        });
    const url = cfg.dsn.replace(/^https:\/\/[^@]+@([^/]+)\/.*$/, `https://$1/api/${cfg.projectId}/envelope/`);
    fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-sentry-envelope" },
        body: envelope,
        keepalive: true
    }).then(res => { if (!res.ok)
        throw new Error("Fetch Failed"); }).catch(() => { try {
        navigator.sendBeacon?.(url, new Blob([envelope], { type: "application/x-sentry-envelope" }));
    }
    catch { } });
}
let inst;
const noop = { configLoaded: () => { }, countryLoaded: () => { }, beginCycle: () => { }, trackBidsComplete: () => { }, trackRenderComplete: () => { }, trackFocusLost: () => { }, trackFocusRestored: () => { }, cleanup: () => { }, setSampleRate: () => { }, setCustomParams: () => { }, trackError: () => { } };
export const configureMetrics = (c) => {
    if (typeof window === "undefined")
        return getMetrics();
    inst?.cleanup();
    try {
        inst = createMetricsService(c);
    }
    catch (e) {
        console.error("[metrics] configure failed", e);
        inst = undefined;
    }
    try {
        window.addEventListener("pagehide", () => { inst?.cleanup(); inst = undefined; }, { once: true, passive: true });
    }
    catch { }
    return getMetrics();
};
export const getMetrics = () => inst ?? noop;
export default {
    configure: configureMetrics,
    isConfigured: () => inst !== undefined,
    configLoaded: (id) => getMetrics().configLoaded(id),
    countryLoaded: (c) => getMetrics().countryLoaded(c),
    beginCycle: (c) => getMetrics().beginCycle(c),
    trackBidsComplete: () => getMetrics().trackBidsComplete(),
    trackRenderComplete: (id) => getMetrics().trackRenderComplete(id),
    trackFocusLost: () => getMetrics().trackFocusLost(),
    trackFocusRestored: () => getMetrics().trackFocusRestored(),
    cleanup: () => { inst?.cleanup(); inst = undefined; },
    setSampleRate: (r) => getMetrics().setSampleRate(r),
    setCustomParams: (p) => getMetrics().setCustomParams(p),
    trackError: (error, tags) => getMetrics().trackError(error, tags)
};
